import type {
  DocGenerationRequestSummaryDto,
  DocumentGenerationStatusDto,
  GeneratedDocumentDto,
  ScenarioTransactionDto,
} from '@/api/hub/scenario/list.schema'
import { scenarioHubService } from '@/api/hub/scenario/service'
import type {
  FGTypeForecastModel,
  MaterialForecastModel,
  Sale1230GetResultModel,
  WeeklyProjectionModel,
} from '@/api/sale/sale1230/get.schema'

import { fetchItemSelectOptions } from '@/api/options/item'
import { ItemSelectOptions } from '@/api/options/item.schema'
import { QueryClient } from '@tanstack/react-query'
import {
  action,
  computed,
  flow,
  makeObservable,
  observable,
  runInAction,
} from 'mobx'

// Define the types for the scenario state
export type TransactionState = ScenarioTransactionDto & {
  isLoading?: boolean
}

// ScenarioStore class
export default class ScenarioStore {
  scenarioService
  queryClient

  // Simplified scenario state
  currentScenarioId: string | null = null
  currentScenarioName: string | null = null
  scenarioTransactions: ScenarioTransactionDto[] = []
  useScenario: boolean = false

  // Error state
  error: Error | null = null

  // Document generation status
  documentGenerationStatus: DocumentGenerationStatusDto | null = null

  // Document generation requests history
  documentGenerationRequests: DocGenerationRequestSummaryDto[] = []

  // Flag to track if handlers have been registered
  private _handlersRegistered = false

  // All item options
  allItems: ItemSelectOptions = []

  constructor(
    scenarioService: typeof scenarioHubService,
    queryClient: QueryClient
  ) {
    makeObservable(this, {
      currentScenarioId: observable,
      currentScenarioName: observable,
      scenarioTransactions: observable,
      useScenario: observable,
      error: observable,
      documentGenerationStatus: observable,
      documentGenerationRequests: observable,
      allItems: observable,
      // Computed properties
      scenarioName: computed,
      scenarioId: computed,
      // Actions and flows
      createScenario: flow,
      addTransaction: flow,
      removeTransaction: flow,
      updateScenarioName: flow,
      requestDocumentGeneration: flow,
      setUseScenario: action,
      setError: action,
      setDocumentGenerationStatus: action,
      setDocumentGenerationRequests: action,
      setScenarioTransactions: action,
      setCurrentScenarioInfo: action,
      getFGTypeForecast: action,
    })

    this.scenarioService = scenarioService
    this.queryClient = queryClient

    // Fetch and set item options
    this.queryClient
      .fetchQuery(fetchItemSelectOptions())
      .then((items) => {
        runInAction(() => {
          this.allItems = items
        })
      })
      .catch((error) => {
        console.error('Failed to fetch item options:', error)
      })

    this.startConnection()
    // Register event handlers when the store is created
    this.registerEventHandlers()
  }

  // Computed properties for easier access to data
  get scenarioName() {
    return this.currentScenarioName || ''
  }

  get scenarioId() {
    return this.currentScenarioId || ''
  }

  // Register event handlers for real-time updates
  private registerEventHandlers() {
    // Only register handlers if not already registered
    if (this._handlersRegistered) return
    this._handlersRegistered = true

    // Handle error messages from the server
    this.scenarioService.handleError((message) => {
      this.setError(new Error(message))
    })

    // Handle scenario updates
    this.scenarioService.handleScenarioUpdated((update) => {
      if (update.scenarioId && update.name) {
        this.setCurrentScenarioInfo(update.scenarioId, update.name)
        this.setScenarioTransactions(update.transactions || [])
      }
    })

    // Handle transaction added events
    this.scenarioService.handleTransactionAdded((result) => {
      if (!result.success) {
        this.setError(
          new Error(result.errorMessage || 'Failed to add transaction')
        )
        return
      }

      if (this.currentScenarioId === result.scenarioId) {
        this.setScenarioTransactions(result.updatedTransactions || [])
      }
    })

    // Handle transaction removed events
    this.scenarioService.handleTransactionRemoved((result) => {
      if (!result.success) {
        this.setError(
          new Error(result.errorMessage || 'Failed to remove transaction')
        )
        return
      }

      if (this.currentScenarioId === result.scenarioId) {
        this.setScenarioTransactions(result.updatedTransactions || [])
      }
    })

    // Handle scenario name updates
    this.scenarioService.handleScenarioNameUpdated((scenarioId, newName) => {
      if (this.currentScenarioId === scenarioId) {
        runInAction(() => {
          this.currentScenarioName = newName
          this.setUseScenario(true)
        })
      }
    })

    // Handle document generation status updates
    this.scenarioService.handleDocumentGenerationUpdated((status) => {
      console.log('📄 Document generation status updated:', status)
      this.setDocumentGenerationStatus(status)
    })

    // Simplified handlers for scenario flow
    this.scenarioService.handleScenarioTransactionsLoaded((transactions) => {
      this.setScenarioTransactions(transactions)
    })

    this.scenarioService.handleScenarioTransactionsUpdated((transactions) => {
      this.setScenarioTransactions(transactions)
    })

    this.scenarioService.handleDocumentGenerationRequestsLoaded((requests) => {
      this.setDocumentGenerationRequests(requests)
    })
  }

  // Initialize the store and start the SignalR connection
  startConnection(onConnected?: () => void) {
    this.scenarioService.startHubConnection(onConnected)
  }

  // Stop the SignalR connection
  stopConnection() {
    this.scenarioService.stopHubConnection()
    runInAction(() => {
      this.currentScenarioId = null
      this.currentScenarioName = null
      this.scenarioTransactions = []
      this.useScenario = false
      this.error = null
      this.documentGenerationStatus = null
    })
  }

  // Leave the current scenario
  leaveScenario() {
    if (this.scenarioId) {
      this.scenarioService.leaveScenario(this.scenarioId)
      runInAction(() => {
        this.setUseScenario(false)
        this.currentScenarioId = null
        this.currentScenarioName = null
        this.scenarioTransactions = []
      })
    }
  }

  // Action methods to set observable state
  setUseScenario(useScenario: boolean) {
    if (this.currentScenarioId) {
      this.useScenario = useScenario
    }
  }

  setError(error: Error | null) {
    this.error = error
  }

  setDocumentGenerationStatus(status: DocumentGenerationStatusDto | null) {
    this.documentGenerationStatus = status
  }

  // Flow methods to interact with the server
  *createScenario(forecastId: string, name: string) {
    try {
      this.setError(null)
      yield this.scenarioService.createScenario(forecastId, name)
    } catch (error) {
      this.setError(
        error instanceof Error ? error : new Error('Failed to create scenario')
      )
    }
  }

  *addTransaction(transaction: Omit<ScenarioTransactionDto, 'id'>) {
    if (!this.scenarioId) {
      this.setError(new Error('No active scenario'))
      return
    }

    try {
      this.setError(null)
      console.log('📥 Adding transaction:', transaction)
      yield this.scenarioService.addTransaction(this.scenarioId, transaction)
    } catch (error) {
      this.setError(
        error instanceof Error ? error : new Error('Failed to add transaction')
      )
    }
  }

  *removeTransaction(transactionId: string) {
    if (!this.scenarioId) {
      this.setError(new Error('No active scenario'))
      return
    }

    try {
      this.setError(null)
      yield this.scenarioService.removeTransaction(
        this.scenarioId,
        transactionId
      )
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error
          : new Error('Failed to remove transaction')
      )
    }
  }

  updateScenarioName(name: string) {
    if (!this.scenarioId) {
      this.setError(new Error('No active scenario'))
      return
    }

    try {
      this.setError(null)
      this.scenarioService.updateScenarioName(this.scenarioId, name)
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error
          : new Error('Failed to update scenario name')
      )
    }
  }

  requestDocumentGeneration(selectedTransactionIds: string[]) {
    if (!this.scenarioId) {
      this.setError(new Error('No active scenario'))
      return
    }

    try {
      this.setError(null)
      // Clear previous status before starting new generation
      this.setDocumentGenerationStatus(null)
      this.scenarioService.requestDocumentGeneration(
        this.scenarioId,
        selectedTransactionIds
      )
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error
          : new Error('Failed to request document generation')
      )
    }
  }

  // Helper method to get generated documents
  getGeneratedDocuments(): GeneratedDocumentDto[] {
    return this.documentGenerationStatus?.generatedDocuments || []
  }

  // Helper method to check if a document generation is in progress
  isDocumentGenerationInProgress(): boolean {
    return (
      this.documentGenerationStatus?.status === 'Pending' ||
      this.documentGenerationStatus?.status === 'Processing'
    )
  }

  // Reset the store state
  resetStore() {
    runInAction(() => {
      this.currentScenarioId = null
      this.currentScenarioName = null
      this.scenarioTransactions = []
      this.useScenario = false
      this.error = null
      this.documentGenerationStatus = null
    })
  }

  // Action methods for scenario flow
  setScenarioTransactions(transactions: ScenarioTransactionDto[]) {
    const convertedCount = transactions.filter(
      (t) => !!t.linkedTransactionId
    ).length
    console.log('📥 Setting scenario transactions:', {
      previousCount: this.scenarioTransactions.length,
      newCount: transactions.length,
      convertedCount,
      newTransactions: transactions,
    })
    this.scenarioTransactions = transactions
  }

  setDocumentGenerationRequests(requests: DocGenerationRequestSummaryDto[]) {
    console.log('📥 Setting document generation requests:', {
      previousCount: this.documentGenerationRequests.length,
      newCount: requests.length,
      newRequests: requests,
    })
    this.documentGenerationRequests = requests
  }

  setCurrentScenarioInfo(scenarioId: string, scenarioName: string) {
    this.currentScenarioId = scenarioId
    this.currentScenarioName = scenarioName
    this.setUseScenario(true)
  }

  // Join scenario method
  joinScenario(scenarioId: string, scenarioName?: string) {
    if (scenarioName) {
      this.setCurrentScenarioInfo(scenarioId, scenarioName)
    } else {
      this.currentScenarioId = scenarioId
    }
    this.scenarioService.joinScenario(scenarioId)
  }

  /**
   * Combines base forecast data with current scenario transactions to create a unified view
   * This method applies scenario transactions to the base forecast data and recalculates balances
   * @param baseForecastData - The base forecast data from the API
   * @returns Combined data structure with scenario modifications applied
   */
  combineWithScenarioData(
    baseForecastData: Sale1230GetResultModel,
    fgItemId?: number,
    rmItemIds?: number[]
  ): Sale1230GetResultModel {
    console.log('🔄 Combining data:', {
      fgItemId: fgItemId,
      rmItemIds: rmItemIds,
      useScenario: this.useScenario,
      scenarioTransactionsCount: this.scenarioTransactions.length,
      scenarioTransactions: this.scenarioTransactions,
    })

    // Filter the base data before creating a deep copy
    const filteredBaseData = {
      ...baseForecastData,
      materialForecasts: baseForecastData.materialForecasts.filter(
        (forecast) => {
          if (fgItemId && forecast.itemId === fgItemId) {
            return true
          }
          if (rmItemIds && rmItemIds.includes(forecast.itemId)) {
            return true
          }
          return !fgItemId && !rmItemIds
        }
      ),
    }

    // Create item properties map for checking trade goods
    const itemPropsMap = new Map(
      this.allItems.map((item) => [
        item.value,
        {
          category: item.category,
          canBePurchased: item.canBePurchased,
        },
      ])
    )

    // Create a deep copy of the filtered data and assign isTradeGood
    const combinedData: Sale1230GetResultModel = {
      ...filteredBaseData,
      materialForecasts: filteredBaseData.materialForecasts.map((forecast) => {
        const itemProps = itemPropsMap.get(String(forecast.itemId))
        return {
          ...forecast,
          // Mark as trade good if it's a Finished Good that can be purchased
          isTradeGood:
            forecast.materialType === 'FinishedGoods' &&
            Boolean(itemProps?.canBePurchased),
          weeklyProjections: forecast.weeklyProjections.map((projection) => ({
            ...projection,
            transactions: projection.transactions.map((t) => ({ ...t })),
          })),
        }
      }),
    }

    // If no scenario is active or no transactions, return filtered data
    if (!this.useScenario || this.scenarioTransactions.length === 0) {
      console.log('📤 Returning filtered data (no scenario or no transactions)')
      return combinedData
    }

    // Apply scenario transactions to the combined data
    this.scenarioTransactions.forEach((transaction) => {
      const materialForecast = combinedData.materialForecasts.find(
        (mf) => mf.itemId === transaction.itemId
      )

      if (materialForecast) {
        const weeklyProjection = materialForecast.weeklyProjections.find(
          (wp) => wp.week === transaction.week
        )

        if (weeklyProjection) {
          // Create a scenario transaction in the forecast format
          const forecastTransaction = {
            id: transaction.id || `scenario-${Date.now()}`,
            itemId: transaction.itemId,
            week: transaction.week,
            qty: transaction.qty,
            transactionType: transaction.transactionType as
              | 'PurchaseOrder'
              | 'WorkOrderProduction'
              | 'SalesOrder',
            documentSourceType: 'Scenario',
            documentSourceId: this.currentScenarioId || 'unknown',
            documentSourceNumber: this.currentScenarioName || 'Scenario',
            calculatedDate: new Date(transaction.calculatedDate),
            status: 'Planned' as const,
            notes: transaction.notes || null,
          }

          // Add the scenario transaction to the projection
          weeklyProjection.transactions.push(forecastTransaction)

          // Recalculate closing balance based on all transactions
          const incoming = weeklyProjection.transactions
            .filter(
              (t) =>
                t.transactionType === 'PurchaseOrder' ||
                t.transactionType === 'WorkOrderProduction'
            )
            .reduce((sum, t) => sum + t.qty, 0)

          const outgoing = weeklyProjection.transactions
            .filter(
              (t) =>
                t.transactionType === 'SalesOrder' ||
                t.transactionType === 'WorkOrderConsumption'
            )
            .reduce((sum, t) => sum + t.qty, 0)

          weeklyProjection.closingBalance =
            weeklyProjection.openingBalance + incoming + outgoing
        }
      }
    })

    // Recalculate subsequent weeks' opening balances for each material
    combinedData.materialForecasts.forEach((forecast) => {
      const sortedProjections = forecast.weeklyProjections.sort(
        (a, b) => a.week - b.week
      )

      for (let i = 1; i < sortedProjections.length; i++) {
        const previousWeek = sortedProjections[i - 1]
        const currentWeek = sortedProjections[i]

        // Current week's opening = previous week's closing
        currentWeek.openingBalance = previousWeek.closingBalance

        // Recalculate current week's closing based on its transactions
        const incoming = currentWeek.transactions
          .filter(
            (t) =>
              t.transactionType === 'PurchaseOrder' ||
              t.transactionType === 'WorkOrderProduction'
          )
          .reduce((sum, t) => sum + t.qty, 0)

        const outgoing = currentWeek.transactions
          .filter(
            (t) =>
              t.transactionType === 'SalesOrder' ||
              t.transactionType === 'WorkOrderConsumption'
          )
          .reduce((sum, t) => sum + t.qty, 0)

        currentWeek.closingBalance =
          currentWeek.openingBalance + incoming + outgoing
      }
    })

    console.log('✅ Combined data result:', {
      materialForecastsCount: combinedData.materialForecasts.length,
      sampleProjections:
        combinedData.materialForecasts[0]?.weeklyProjections?.slice(0, 2),
    })

    return combinedData
  }

  /**
   * Gets scenario transactions for a specific item and week
   */
  getScenarioTransactionsForCell(
    itemId: number,
    week: number
  ): ScenarioTransactionDto[] {
    return this.scenarioTransactions.filter(
      (transaction) =>
        transaction.itemId === itemId && transaction.week === week
    )
  }

  /**
   * Checks if a specific cell has scenario modifications
   */
  hasScenarioModifications(itemId: number, week: number): boolean {
    return this.getScenarioTransactionsForCell(itemId, week).length > 0
  }

  getFGTypeForecast(
    combinedData: Sale1230GetResultModel
  ): FGTypeForecastModel[] {
    // Filter only FinishedGoods
    const fgItems = combinedData.materialForecasts.filter(
      (item) => item.materialType === 'FinishedGoods'
    )

    // Create maps for item properties from allItems
    const itemPropsMap = new Map(
      this.allItems.map((item) => [
        item.value,
        {
          type: item.type,
          canBePurchased: item.canBePurchased,
          category: item.category,
        },
      ])
    )

    // Filter items by matching them with allItems and their types
    const mpItems = fgItems.filter((item) => {
      const itemProps = itemPropsMap.get(String(item.itemId))
      // Regular manufactured FG items (MP type, not purchasable)
      return itemProps?.type === 'MP' && !itemProps.canBePurchased
    })

    const hdItems = fgItems.filter((item) => {
      const itemProps = itemPropsMap.get(String(item.itemId))
      // Regular manufactured FG items (HD type, not purchasable)
      return itemProps?.type === 'HD' && !itemProps.canBePurchased
    })

    // Merge weekly projections for each type
    const mpForecast = {
      type: 'MP',
      materialType: 'FinishedGoods' as const,
      isTradeGood: false,
      weeklyProjections: this.mergeWeeklyProjections(mpItems),
    }

    const hdForecast = {
      type: 'HD',
      materialType: 'FinishedGoods' as const,
      isTradeGood: false,
      weeklyProjections: this.mergeWeeklyProjections(hdItems),
    }

    return [mpForecast, hdForecast]
  }

  private mergeWeeklyProjections(items: MaterialForecastModel[]) {
    const weeks = new Set<number>()
    items.forEach((item: MaterialForecastModel) =>
      item.weeklyProjections.forEach((wp: WeeklyProjectionModel) =>
        weeks.add(wp.week)
      )
    )

    return Array.from(weeks)
      .sort((a, b) => a - b)
      .map((week) => {
        const weekProjections = items
          .flatMap((item: MaterialForecastModel) => item.weeklyProjections)
          .filter((wp: WeeklyProjectionModel) => wp.week === week)

        return {
          itemId: 0, // Group projection doesn't have a specific itemId
          week,
          openingBalance: weekProjections.reduce(
            (sum, wp: WeeklyProjectionModel) => sum + wp.openingBalance,
            0
          ),
          closingBalance: weekProjections.reduce(
            (sum, wp: WeeklyProjectionModel) => sum + wp.closingBalance,
            0
          ),
          transactions: weekProjections.flatMap(
            (wp: WeeklyProjectionModel) => wp.transactions
          ),
        }
      })
  }
}
